/** @jsx jsx */
import React, {
	useCallback,
	useMemo,
	useState,
	useEffect,
	type SyntheticEvent,
	useRef,
	Fragment,
} from 'react';
import { cssMap, jsx } from '@compiled/react';
import omit from 'lodash/omit';
import Icon from '@atlaskit/icon';
import ArchiveIcon from '@atlaskit/icon/core/migration/archive-box--archive';
import BoardsIcon from '@atlaskit/icon/core/migration/board';
import BulletListIcon from '@atlaskit/icon/core/migration/list-bulleted--bullet-list';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import CodeIcon from '@atlaskit/icon/core/migration/angle-brackets--code';
import DashboardIcon from '@atlaskit/icon/core/migration/dashboard';
import GlobeIcon from '@atlaskit/icon/core/globe';
import GettingStartedPageIcon from '@atlaskit/icon/core/migration/compass--discover';
import EmailIcon from '@atlaskit/icon/core/migration/email';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import ReportsIcon from '@atlaskit/icon/core/migration/chart-bar--graph-bar';
import RaiseRequestIcon from '@atlaskit/icon/core/migration/plus-square--issue-raise';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import LabelIcon from '@atlaskit/icon/core/migration/tag--label';
import PeopleGroupIcon from '@atlaskit/icon/core/migration/people-group';
import QueuesIcon from '@atlaskit/icon/core/migration/pages--queues';
import IconSettings from '@atlaskit/icon/core/migration/settings';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightManager } from '@atlaskit/onboarding';
import {
	NavigationFooter,
	NavigationHeader,
	NestableNavigationContent,
	Section,
	SideNavigation,
	SkeletonItem,
} from '@atlaskit/side-navigation';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	EntryKeys,
	ENTRYPOINT_ID_JIRA_PLATFORM,
} from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { Shortcuts } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/main.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { SectionWithHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/section-with-header/index.tsx';
import { SpotlightTarget } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/spotlight-target/index.tsx';
import { useNavigationAutomationLegacyEnabledResource } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/navigation-automation-resource/index.tsx';
import {
	useProject,
	type UseProjectReturn,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import {
	ContextualAnalyticsData,
	IssuesContextualAnalyticsData,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { Issues } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/issues/main.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { ProjectHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-header/main.tsx';
import { shouldRenderNesting } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/should-render/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import {
	matchPartialUrlPathname,
	matchUrlPathname,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { ConnectMenu } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { useProjectReportsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-reports/index.tsx';
import { useProjectSettingsConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-settings/index.tsx';
import { useProjectConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/index.tsx';
import { useProjectSettingsForgeAppsForMenu } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project-settings/index.tsx';
import { useProjectForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project/index.tsx';
import { useServiceDeskQueueForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/servicedesk-queue/index.tsx';
import { ForgeProjectSection } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/project-section/index.tsx';
import { ConnectAppsContainer } from '@atlassian/jira-project-settings-apps-connect-apps/src/baskets/connect-apps/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import {
	JSM_BOARDS_NAV_PROMOTION_KEY,
	INVITE_USERS_PROMPTS_KEY,
} from '@atlassian/jira-service-management-adoption-nudges/src/common/constants.tsx';
import { useShouldRenderInviteTeamPromptSpotlight } from '@atlassian/jira-service-management-adoption-nudges/src/services/index.tsx';
import { InviteUserPrompt } from '@atlassian/jira-service-management-adoption-nudges/src/ui/index.tsx';
import { useIsGettingStartedPageEnabled } from '@atlassian/jira-servicedesk-getting-started-page/src/services/index.tsx';
import { AlertsGlyph } from '@atlassian/jira-servicedesk-incident-management-common/src/assets/index.tsx';
import { ViewsNavItemOnboarding } from '@atlassian/jira-servicedesk-views-onboarding/src/ui/views-nav-item-onboarding/index.tsx';
import { ServicesGlyph } from '@atlassian/jira-servicedesk-work-category/src/common/assets/glyph.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useResource, useRouter } from '@atlassian/react-resource-router';
import type { QueueResourceCustomContext } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/types.tsx';
import type { UseProjectReportsConnectItemsReturn } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-reports/types.tsx';
import type { UseProjectSettingsConnectItemsReturn } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project-settings/types.tsx';
import type { UseProjectConnectItemsReturn } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/types.tsx';
import type {
	UseResourceCustomContext,
	UseResourceReturn,
} from '@atlassian/jira-resource-with-custom-router-context/src/controllers/types.tsx';
import { OnCallGlyph } from '../../common/assets/glyph.tsx';
import { MENU_ID_SD, SECTION_ID } from '../../common/constants.tsx';
import { messages } from '../../common/messages.tsx';
import { CustomerChannelsItem } from '../../common/ui/customer-channels-item/index.tsx';
import { CustomerSupportManagementItems } from '../../common/ui/customer-support-management-section/index.tsx';
import { InviteTeamItemNew } from '../../common/ui/invite-team-item-v2/index.tsx';
import { InviteTeamItem } from '../../common/ui/invite-team-item/index.tsx';
import { KnowledgeItem } from '../../common/ui/knowledge-item/main.tsx';
import { PracticeQueues } from '../../common/ui/practice-queues/main.tsx';
import { Reports } from '../../common/ui/reports/main.tsx';
import { SettingsClassic } from '../../common/ui/settings-classic/main.tsx';
import { SettingsNextgen } from '../../common/ui/settings-nextgen/index.tsx';
import { useFeatures, useSettingFeatures } from '../../controllers/features/main.tsx';
import { useIsPremiumTenant } from '../../controllers/is-premium-tenant/index.tsx';
import { useNestingStack } from '../../controllers/nesting-stack/index.tsx';
import { useViewsFeature } from '../../controllers/views-feature/index.tsx';
import type { Features, UseSettingFeaturesReturn } from '../../controllers/features/types.tsx';
import { Footer } from './footer/index.tsx';

const styles = cssMap({
	menuItem: {
		// Need to increase specificity here until @atlaskit/menu has been migrated to @compiled/react
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&&': {
			height: '44px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-after]': {
			opacity: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-item-elem-after], &:focus-within [data-item-elem-after]': {
			opacity: 1,
		},
	},
});

export const useThrowFeatures: (
	customContext?: UseResourceCustomContext | undefined,
) => UseResourceReturn<Features> = withThrowOnError(useFeatures);
export const useThrowSettingFeatures: (
	customContext?: UseResourceCustomContext | undefined,
) => UseSettingFeaturesReturn = withThrowOnError(useSettingFeatures);
export const useThrowProject: (
	customContext?: QueueResourceCustomContext | undefined,
) => UseProjectReturn = withThrowOnError(useProject);
export const useThrowProjectConnectItems: () => UseProjectConnectItemsReturn =
	withThrowOnError(useProjectConnectItems);
export const useThrowProjectReportsConnectItems: () => UseProjectReportsConnectItemsReturn =
	withThrowOnError(useProjectReportsConnectItems);
export const useThrowProjectSettingsConnectItems: () => UseProjectSettingsConnectItemsReturn =
	withThrowOnError(useProjectSettingsConnectItems);

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/jsm-project-menu
 */
export const Menu = (): JSX.Element => {
	const { formatMessage } = useIntl();
	const [forgeQueueParentStack] = useState(() => [SECTION_ID.QUEUES]);
	const [forgeSettingsParentStack, setForgeSettingsParentStack] = useState<string[]>(() => [
		SECTION_ID.SETTINGS,
	]);
	const isPremiumTenant = useIsPremiumTenant();
	const { data: features } = useThrowFeatures();
	const { data: settingFeatures } = useThrowSettingFeatures();
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const { data: project } = useThrowProject();
	const { data: projectConnectItems, stack: connectProjectStack } = useThrowProjectConnectItems();
	const { data: connectReportsItems, stack: connectReportsStack } =
		useThrowProjectReportsConnectItems();

	const { data: connectSettingsItems, stack: connectSettingsStack } =
		useThrowProjectSettingsConnectItems();
	const { data: forgeSettingsItems, stack: forgeProjectSettingsStack } =
		useProjectSettingsForgeAppsForMenu(
			'project-settings-servicedesk-forge-apps',
			forgeSettingsParentStack,
		);
	const { stack: forgeProjectStack } = useProjectForgeApps('forge-spa-project');
	const { stack: forgeProjectServicedeskStack } = useProjectForgeApps(
		'forge-spa-project-servicedesk',
	);
	const { stack: forgeQueueStack } = useServiceDeskQueueForgeApps(
		'forge-spa-servicedesk-queue',
		forgeQueueParentStack,
	);
	const ecosystemItems = useMemo(
		() => ({
			forge: forgeSettingsItems,
			connect: connectSettingsItems,
		}),
		[connectSettingsItems, forgeSettingsItems],
	);

	const { enabled: isLegacyAutomationEnabled } =
		useNavigationAutomationLegacyEnabledResource().data || {};

	const [stack, setStack] = useNestingStack({
		connectStack: connectProjectStack || connectReportsStack || connectSettingsStack,
		forgeStack:
			forgeProjectStack ||
			forgeProjectServicedeskStack ||
			forgeProjectSettingsStack ||
			forgeQueueStack,
		knowledgeType: features && features.knowledge.type,
		isLegacyAutomationEnabled: !!isLegacyAutomationEnabled,
		isTeamManagedProject: project?.isSimplified ?? false,
	});

	const { data: projectContext } = useResource(projectContextResource);
	const {
		shouldUseJSM,
		loading: isOgTenantInfoLoading,
		error: ogTenantInfoError,
	} = useOgTenantInfo();

	const cloudId = useCloudId();
	const [{ route }] = useRouter();
	const [isLoading, isGettingStatedPageEnabled] = useIsGettingStartedPageEnabled();
	const { isInviteTeamSpotlightEnabled, loadInviteTeamResource, onInviteTeamOnboardingCTAClick } =
		useShouldRenderInviteTeamPromptSpotlight();

	useEffect(() => {
		project?.isSimplified
			? setForgeSettingsParentStack([SECTION_ID.SETTINGS, 'apps'])
			: setForgeSettingsParentStack([SECTION_ID.SETTINGS]);
	}, [project?.isSimplified]);

	const feedbackButtonRef = useRef<HTMLButtonElement>(null);

	const onSubmitFeedbackClick = useCallback(() => {
		const additionalFields = [{ id: EntryKeys.CLOUD_ID, value: cloudId }];
		openFeedbackCollector({
			feedbackGroupId: 'jira-servicedesk-classic',
			entrypointId: ENTRYPOINT_ID_JIRA_PLATFORM,
			feedbackContext: additionalFields,
			additionalFields,
			triggerRef: feedbackButtonRef,
		});
	}, [openFeedbackCollector, cloudId]);

	const viewsFeature = useViewsFeature({ features });

	if (features == null || project == null || projectConnectItems == null || isOgTenantInfoLoading) {
		return <SkeletonItem />;
	}

	const urlPath = `/jira/servicedesk/projects/${project.key}`;
	const classicSettingsUrl = `/jira/servicedesk/projects/${project.key}/settings/details`;

	const BackMenuItem = {
		href: urlPath,
		onClick: (ev: SyntheticEvent) => ev.preventDefault(),
		title: formatMessage(
			project.isSimplified ? messages.backToServiceProject : messages.backToProject,
		),
	};

	const knowledgeInsightsTitle =
		// Returns true if any Knowledge & Insights elements exist.
		shouldRenderNesting(
			features.knowledge.enabled && !!features.knowledge.type,
			stack,
			SECTION_ID.KNOWLEDGE,
		) || shouldRenderNesting(features.hasReports, stack, SECTION_ID.REPORTS)
			? formatMessage(messages.knowledgeInsights)
			: undefined;

	const isServicesEnabled = Boolean(settingFeatures?.hasServices);

	const isJSMAlertOnCallEnabled = shouldUseJSM && !ogTenantInfoError;

	const isAlertsEnabled = features.opsgenieAlert.enabled && !!features.opsgenieAlert.url;
	const isOncallEnabled = features.opsgenieOnCall.enabled && !!features.opsgenieOnCall.url;

	const hasOperationsSectionItems = isServicesEnabled || isAlertsEnabled || isOncallEnabled;
	const isCustomerSupportManagementEnabled = Boolean(settingFeatures?.hasCustomerSupportManagement);
	const isDeveloperEscalationsEnabled = Boolean(settingFeatures?.hasDeveloperEscalation);
	const isProductsAndEntitlementsEnabled = Boolean(settingFeatures?.hasProductsAndEntitlements);
	const hasNotificationLogs = fg('jcs_master_flag') ? !!features.hasNotificationLogs : true;

	const SettingsMenuItem = shouldRenderNesting(
		features.hasSettings,
		stack,
		SECTION_ID.SETTINGS,
	) && (
		<NestingMenuItem
			id={SECTION_ID.SETTINGS}
			analytics={{ itemId: 'settings' }}
			selectedOn={matchPartialUrlPathname()}
			href={project.isSimplified ? `${urlPath}/settings` : classicSettingsUrl}
			iconBefore={<IconSettings spacing="spacious" label="" />}
			overrides={{ BackMenuItem: omit(BackMenuItem, ['onClick']) }}
			data-testId="navigation-apps-sidebar-servicedesk.ui.menu.project-settings"
			title={formatMessage(
				project.isSimplified
					? messages.serviceProjectSettings
					: messages.projectSettingsRetranslate,
			)}
		>
			{project.isSimplified ? (
				<SettingsNextgen stack={stack} />
			) : (
				<SettingsClassic
					ecosystemItems={ecosystemItems}
					projectId={project.id}
					projectKey={project.key}
					stack={stack}
				/>
			)}
		</NestingMenuItem>
	);

	const HelpCenterSettingsMenuItem = features?.hasHelpCenterSettings && fg('jcs_master_flag') && (
		<MenuItem
			analytics={{ itemId: 'helpCenterSettings' }}
			href={`/jira/helpcenter/projects/${project.key}/settings`}
			iconBefore={<ShortcutIcon spacing="spacious" label="" />}
		>
			{formatMessage(
				fg('jcs_project_type_m3') ? messages.customerHubSettings : messages.serviceHubSettings,
			)}
		</MenuItem>
	);

	const NotificationLogsMenuItem = hasNotificationLogs && (
		<MenuItem
			analytics={{ itemId: 'notificationLogs' }}
			href={`${urlPath}/notification-logs`}
			iconBefore={<EmailIcon spacing="spacious" label="" />}
			selectedOn={['notification-logs']}
		>
			{formatMessage(messages.notificationLogs)}
		</MenuItem>
	);

	const FeedbackMenuItem = (
		<MenuItem
			analytics={{ itemId: 'feedback' }}
			iconBefore={<FeedbackIcon spacing="spacious" label="" />}
			onClick={onSubmitFeedbackClick}
			ref={feedbackButtonRef}
		>
			{formatMessage(messages.feedback)}
		</MenuItem>
	);

	const ShortcutsMenuItem = (
		<Shortcuts
			canAddItem={features.hasShortcutsAdd}
			canAddLinks
			canAddRepositories={false}
			isSimplifiedProject={project.isSimplified}
			projectKey={project.key}
			projectId={project.id}
		/>
	);

	const KnowledgeMenuItem = shouldRenderNesting(
		features.knowledge.enabled && !!features.knowledge.type,
		stack,
		SECTION_ID.KNOWLEDGE,
	) && (
		<KnowledgeItem
			href={`${urlPath}/knowledge`}
			nestingId={SECTION_ID.KNOWLEDGE}
			overrides={{ BackMenuItem }}
			stack={stack}
			type={features.knowledge.type}
		/>
	);

	const ReportsMenuItem = shouldRenderNesting(features.hasReports, stack, SECTION_ID.REPORTS) && (
		<NestingMenuItem
			analytics={{ itemId: 'reports' }}
			href={`${urlPath}/reports`}
			iconBefore={<ReportsIcon spacing="spacious" label="" />}
			id={SECTION_ID.REPORTS}
			overrides={{ BackMenuItem }}
			selectedOn={matchPartialUrlPathname()}
			title={formatMessage(messages.itemReports)}
		>
			<Reports projectKey={project.key} connectItems={connectReportsItems} />
		</NestingMenuItem>
	);

	const CustomerChannelsMenuItem = features.hasCustomerChannels && (
		<CustomerChannelsItem>{formatMessage(messages.itemCustomerChannels)}</CustomerChannelsItem>
	);

	const jsmProjectSummary = (fg('jsm-project-summary-beta')
		? features.hasProjectSummary
		: fg('jsm-project-summary')) && (
		<MenuItem
			analytics={{ itemId: 'project-summary' }}
			href={`${urlPath}/summary`}
			iconBefore={
				fg('jsm_project_summary_milestone_2') ? (
					<GlobeIcon label="" />
				) : (
					<DashboardIcon spacing="spacious" label="" />
				)
			}
			selectedOn={['servicedesk-project-summary']}
		>
			{formatMessage(messages.itemProjectSummary)}
		</MenuItem>
	);

	const JsmViewsOnboarding = fg('jsm_views_of_work_phase_1_onboarding')
		? ViewsNavItemOnboarding
		: Fragment;
	const jsmViews = viewsFeature.isEnabled && fg('jsm_views_of_work_phase_1') && (
		<JsmViewsOnboarding>
			<MenuItem
				analytics={{ itemId: 'views' }}
				href={viewsFeature.url}
				iconBefore={<BulletListIcon spacing="spacious" label="" />}
				selectedOn={['servicedesk-boards', 'servicedesk-calendar']}
			>
				{formatMessage(messages.itemViews)}
			</MenuItem>
		</JsmViewsOnboarding>
	);

	const jsmKanbanBoard = features.jsmBoard?.enabled && !fg('jsm_views_of_work_phase_1') && (
		<>
			<SpotlightTarget name={JSM_BOARDS_NAV_PROMOTION_KEY}>
				<MenuItem
					analytics={{ itemId: 'boards' }}
					href={features.jsmBoard.url}
					iconBefore={<BoardsIcon spacing="spacious" label="" />}
					selectedOn={['servicedesk-boards']}
				>
					{formatMessage(messages.itemBoards)}
				</MenuItem>
			</SpotlightTarget>
		</>
	);

	const jsmCalendar = features.jsmCalendar?.enabled && !fg('jsm_views_of_work_phase_1') && (
		<MenuItem
			analytics={{ itemId: 'calendar' }}
			href={features.jsmCalendar.url}
			iconBefore={<CalendarIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-calendar']}
		>
			{formatMessage(messages.itemCalendars)}
		</MenuItem>
	);

	const GettingStartedPageMenuItem = !isLoading && isGettingStatedPageEnabled && (
		<MenuItem
			analytics={{ itemId: 'getting-started-page' }}
			href={`${urlPath}/getting-started`}
			iconBefore={<GettingStartedPageIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-getting-started-page']}
		>
			{formatMessage(messages.gettingStarted)}
		</MenuItem>
	);

	const developerEscalations = isDeveloperEscalationsEnabled && (
		<MenuItem
			analytics={{ itemId: 'developerEscalations' }}
			href={`${urlPath}/escalations`}
			iconBefore={<CodeIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-customer-service-escalations']}
		>
			{formatMessage(messages.itemDeveloperEscalations)}
		</MenuItem>
	);

	const products = isProductsAndEntitlementsEnabled && (
		<MenuItem
			analytics={{ itemId: 'products' }}
			href={`${urlPath}/products`}
			iconBefore={<LabelIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-customer-service-products']}
		>
			{formatMessage(messages.itemProducts)}
		</MenuItem>
	);

	const CustomersMenuItem = features.hasCustomers && !isCustomerSupportManagementEnabled && (
		<MenuItem
			analytics={{ itemId: 'customers' }}
			href={`${urlPath}/customers`}
			iconBefore={<PeopleGroupIcon spacing="spacious" label="" />}
			selectedOn={['servicedesk-customers-v2']}
		>
			{formatMessage(messages.itemCustomers)}
		</MenuItem>
	);
	const footer = (() => (
		<NavigationFooter>
			<Footer isSimplified={project.isSimplified} />
		</NavigationFooter>
	))();

	const inviteTeamItem =
		features.hasSettings && expValEquals('jsm_core_invites_experiment', 'cohort', 'experiment') ? (
			<InviteTeamItemNew>{formatMessage(messages.itemAddPeople)}</InviteTeamItemNew>
		) : (
			<InviteTeamItem loadEntryPointResource={loadInviteTeamResource}>
				{formatMessage(messages.itemInviteAgents)}
			</InviteTeamItem>
		);

	return (
		<ContextualAnalyticsData menuId={MENU_ID_SD} stack={stack}>
			<ConnectAppsContainer onError={undefined} scope={project.key}>
				<SideNavigation
					label=""
					isServer={__SERVER__}
					isSSRPlaceholderEnabled={
						// eslint-disable-next-line jira/ff/no-preconditioning
						(fg('add_ssr_placeholder_replacements_to_nin_and_nav') &&
							route?.name === 'servicedesk-issues') ||
						(route.name === 'issue' && fg('add_ssr_placeholder_replacements_to_nav_for_iv'))
					}
				>
					<NavigationHeader>
						<ProjectHeader
							description={formatMessage(messages.headerDescriptionJsm)}
							avatarUrl={project.avatarUrl}
							name={project.name}
						/>
					</NavigationHeader>
					<NestableNavigationContent stack={stack} onChange={setStack}>
						<SectionWithHeader
							title={formatMessage(messages.sideBarNavigationTitle)}
							headingLevel={2}
							screenReaderTextHeading
						>
							{GettingStartedPageMenuItem}
							{jsmProjectSummary}
							{shouldRenderNesting(features.hasQueues, stack, SECTION_ID.QUEUES) && (
								<NestingMenuItem
									analytics={{ itemId: 'queues' }}
									href={`${urlPath}/queues`}
									iconBefore={<QueuesIcon spacing="spacious" label="" />}
									id={SECTION_ID.QUEUES}
									overrides={{ BackMenuItem }}
									selectedOn={matchPartialUrlPathname()}
									title={formatMessage(messages.itemQueuesRetranslate)}
									onClick={(ev: SyntheticEvent) => ev.preventDefault()}
								>
									<PracticeQueues title={formatMessage(messages.itemQueuesRetranslate)} />
								</NestingMenuItem>
							)}
							{jsmViews}
							{jsmKanbanBoard}
							{jsmCalendar}
							{developerEscalations}
							{features.raiseRequest.enabled && (
								<MenuItem
									analytics={{ itemId: 'raise-request' }}
									iconBefore={<RaiseRequestIcon spacing="spacious" label="" />}
									href={features.raiseRequest.url}
									target="_blank"
								>
									{formatMessage(messages.itemRaiseRequest)}
								</MenuItem>
							)}

							{hasOperationsSectionItems && (
								<Section title={formatMessage(messages.operations)}>
									{isServicesEnabled && (
										<MenuItem
											analytics={{ itemId: 'services' }}
											href={`${urlPath}/services`}
											// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
											iconBefore={<Icon glyph={ServicesGlyph} label="" />}
											selectedOn={matchUrlPathname()}
										>
											{formatMessage(messages.services)}
										</MenuItem>
									)}
									{isAlertsEnabled && (
										<MenuItem
											analytics={{ itemId: 'opsgenie-alert' }}
											css={styles.menuItem}
											// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
											iconBefore={<Icon glyph={AlertsGlyph} label="" />}
											href="/jira/ops/alerts?view=list&query="
											iconAfter={<ShortcutIcon spacing="spacious" label="" />}
											target="_blank"
											{...(!isJSMAlertOnCallEnabled && {
												href: features.opsgenieAlert.url,
											})}
										>
											{formatMessage(messages.alerts)}
											<ScreenReaderText>
												{formatMessage(messages.linkOpensInNewWindow)}
											</ScreenReaderText>
										</MenuItem>
									)}
									{isOncallEnabled && (
										<MenuItem
											analytics={{ itemId: 'opsgenie-on-call' }}
											css={styles.menuItem}
											// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
											iconBefore={<Icon glyph={OnCallGlyph} label="" />}
											selectedOn={matchPartialUrlPathname()}
											iconAfter={<ShortcutIcon spacing="spacious" label="" />}
											target="_blank"
											href="/jira/ops/who-is-on-call"
											{...(!isJSMAlertOnCallEnabled && {
												href: `${urlPath}/on-call-schedule`,
												iconAfter: undefined,
												target: undefined,
											})}
										>
											{formatMessage(messages.onCall)}
										</MenuItem>
									)}
								</Section>
							)}

							{shouldRenderNesting(features.hasPIN, stack, SECTION_ID.ISSUES) && (
								<IssuesContextualAnalyticsData navigatorScope="project">
									<NestingMenuItem
										analytics={{ itemId: 'project-issue-navigator' }}
										href={`/jira/servicedesk/projects/${project.key}/issues`}
										iconBefore={<IssuesIcon spacing="spacious" label="" />}
										id={SECTION_ID.ISSUES}
										overrides={{ BackMenuItem }}
										selectedOn={matchPartialUrlPathname()}
										title={formatMessage(messages.itemIssues)}
									>
										<Issues
											projectKey={project.key}
											pinUrl={`/jira/servicedesk/projects/${project.key}/issues/?filter=`}
										/>
									</NestingMenuItem>
								</IssuesContextualAnalyticsData>
							)}

							<ForgeProjectSection overrides={{ BackMenuItem }} />

							<ConnectMenu items={projectConnectItems} showIcons overrides={{ BackMenuItem }} />

							<SectionWithHeader title={knowledgeInsightsTitle} headingLevel={3}>
								{KnowledgeMenuItem}
								{ReportsMenuItem}
							</SectionWithHeader>

							{projectContext?.isProjectArchived === true ? null : (
								<SectionWithHeader title={formatMessage(messages.directory)} headingLevel={3}>
									{fg('jsm_customers_directory_page_for_all')
										? features.hasCustomers && (
												<CustomerSupportManagementItems
													urlPath={urlPath}
													isPremiumTenant={isPremiumTenant}
												/>
											)
										: isCustomerSupportManagementEnabled && (
												<CustomerSupportManagementItems
													urlPath={urlPath}
													isPremiumTenant={isPremiumTenant}
												/>
											)}
									{products}
									{CustomerChannelsMenuItem}
									{stack.length === 0 && isInviteTeamSpotlightEnabled ? (
										<SpotlightManager blanketIsTinted={false}>
											<SpotlightTarget name={INVITE_USERS_PROMPTS_KEY}>
												{inviteTeamItem}
											</SpotlightTarget>
											<InviteUserPrompt onCTAClick={onInviteTeamOnboardingCTAClick} />
										</SpotlightManager>
									) : (
										inviteTeamItem
									)}
									{!fg('jsm_customers_directory_page_for_all') && CustomersMenuItem}
								</SectionWithHeader>
							)}

							<SectionWithHeader title={formatMessage(messages.shortcuts)} headingLevel={3}>
								{ShortcutsMenuItem}
							</SectionWithHeader>

							<Section hasSeparator>
								{HelpCenterSettingsMenuItem}
								{SettingsMenuItem}
								{NotificationLogsMenuItem}
								{!project.isSimplified && FeedbackMenuItem}
							</Section>
							{(fg('archived_issues_in_nav4_sidebar')
								? features?.hasArchivedIssues === true
								: true) && (
								<Section hasSeparator>
									<MenuItem
										analytics={{ itemId: 'archives' }}
										href={
											fg('slv-3_migrate_archived_issues_routes')
												? `/jira/servicedesk/projects/${project.key}/archived-work-items`
												: `/jira/servicedesk/projects/${project.key}/issues/archive`
										}
										iconBefore={<ArchiveIcon spacing="spacious" label="" />}
										iconAfter={
											<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
										}
										selectedOn={
											fg('slv-3_migrate_archived_issues_routes')
												? ['servicedesk-archived-work-items']
												: ['archived-issues-directory']
										}
										testId="navigation-apps-sidebar-servicedesk.ui.menu.archives-link"
									>
										{formatMessage(messages.itemArchives)}
									</MenuItem>
								</Section>
							)}
						</SectionWithHeader>
					</NestableNavigationContent>
					{footer}
				</SideNavigation>
			</ConnectAppsContainer>
		</ContextualAnalyticsData>
	);
};
